import { Chip, type ChipProps } from './Chip';
import { styled } from '@mui/material';
import type { ComboBoxApi } from 'components/inputs/types';
import { useGet } from 'lib/api';
import type { Response } from 'types';

export type ChipsListProps = {
  chips: ChipProps[];
  onDelete?: (chip: ChipProps) => void;
  api?: ComboBoxApi;
};

/**
 * ChipsList component
 * @param chips - the chips to display
 * @param onDelete - callback function to be called when a chip is deleted
 * @param api - the api to fetch the chips from
 */
export const ChipsList = ({ chips, onDelete, api }: ChipsListProps) => {
  // Helper to resolve vars (can be array or function)
  const getVars = () => {
    if (!api?.vars && api?.search) return [''];
    if (!api?.vars) return [];
    return typeof api.vars === 'function' ? api.vars() : api.vars;
  };

  const { data } = useGet<Response<ChipProps[]>>(
    api?.url || null,
    api?.key || '',
    getVars(),
    undefined,
    true,
    {
      enabled: !!api,
    }
  );

  const transform = (apiData: Response<ChipProps[]>) => {
    if (api) {
      const transformed = api.responseTransform(apiData);
      return chips.map(chip => ({
        ...chip,
        label: transformed?.find(d => d.id.toString() === chip.id?.toString())?.label || chip.label,
      }));
    }
    return [];
  };

  const chipsData = data && api ? transform(data) : chips;

  const handleDelete = (chipToDelete: ChipProps) => () => {
    onDelete && onDelete(chipToDelete);
  };

  return (
    <List>
      {chipsData.map(chip => {
        return <Chip component="li" key={chip.id} {...chip} onDelete={handleDelete(chip)} />;
      })}
    </List>
  );
};

const List = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(3),
}));
